import { Fragment, useEffect } from 'react';

import { Box, ButtonRoundedPrimary, Caption, LabelBold, Spinner } from '@comeon/ui';

import { NOTIFICATION_TYPE, showNativeNotification } from '~/components/app-bridge';
import { isNativeNotificationEnabled } from '~/components/app-bridge/app-properties';
import SidebarHeader from '~/components/header/sidebar-header';
import Icon, { ICON_UPLOAD_LINE } from '~/components/icon';
import { NOTIFICATION_ID } from '~/components/limits/spending-limit/utils/constant';
import {
	FlowType,
	spendingLimitStoragekey,
	trackSpendingLimitEvent
} from '~/components/limits/spending-limit/utils/tracking';
import { MainTitle, SubTitle } from '~/components/misc/section-head';
import { hideQueuedNotification, showQueuedNotification } from '~/components/notifications/ducks/actions';
import { showSupportDialogBox } from '~/components/support/ducks';
import useTranslations from '~/components/translation/use-translations';
import { useAppDispatch } from '~/store/types';

import { FILE_SIZE, textKeys } from './constants';
import useFetchDocs from './hooks/use-fetch-docs';
import useUploadDoc from './hooks/use-upload-doc';
import {
	StyledButtonContainer,
	StyledContactSupport,
	StyledContainer,
	StyledContent,
	StyledDocument,
	StyledDocumentContainer,
	StyledFileName,
	StyledInnerContainer,
	StyledInput,
	StyledLabel,
	StyledLinkButton,
	StyledOrText,
	StyledScrollContainer,
	StyledUploadButton,
	StyledUploadList
} from './styled';
import { DocumentUploadProps } from './types';

const SpendingLimitUploadDocContent: React.FC<DocumentUploadProps> = ({
	onClose,
	onComplete,
	flow = FlowType.popup
}) => {
	const dispatch = useAppDispatch();

	const { data: documents, loading: isFetchLoading } = useFetchDocs();
	const { docs, loading: isUploadLoading, inputRef, onInputChange, queryFn: onUpload } = useUploadDoc(flow);
	const { translations: t, isLoadingTranslations } = useTranslations(textKeys);

	const onSupport = () => {
		trackSpendingLimitEvent({ flow, label: 'Click contact support' });
		void dispatch(showSupportDialogBox(true));
	};

	const onContinue = () => {
		trackSpendingLimitEvent({ flow, label: `Click upload docs successful - Docs: ${docs.length}` });
		const message = t.MOBILE_UPLOAD_DOCUMENTS_PENDING;

		if (isNativeNotificationEnabled()) {
			showNativeNotification(NOTIFICATION_TYPE.SPENDING_LIMIT, message);
		} else {
			dispatch(hideQueuedNotification(NOTIFICATION_ID));
			dispatch(
				showQueuedNotification({
					message,
					color: 'neutral',
					autoHide: true,
					autoHideDuration: 5000,
					openInSidebar: true,
					id: NOTIFICATION_ID
				})
			);
		}

		onComplete?.();
		onClose();
	};

	useEffect(() => {
		// To be consumed in socket response
		sessionStorage.setItem(spendingLimitStoragekey, flow);
		trackSpendingLimitEvent({ flow, label: 'Upload Documents page displayed' });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (isLoadingTranslations) {
		<Spinner />;
	}

	return (
		<StyledContainer>
			{/* @ts-expect-error - $FixMe sidebar-header is js file */}
			<SidebarHeader closeIcon onClose={() => onClose()} />
			<StyledContent>
				<StyledScrollContainer>
					<Box textAlign="center" pb={7}>
						<Icon className="documents" type={ICON_UPLOAD_LINE} />
						<MainTitle dataAt="section-main-title-upload-account">
							{t.MOBILE_UPLOAD_DOCUMENTS_HEAD_TITLE}
						</MainTitle>
						<SubTitle dataAt="section-sub-title-upload-account">
							{t.MOBILE_UPLOAD_DOCUMENTS_AFF_SUBTITLE}
						</SubTitle>
					</Box>

					<StyledDocumentContainer>
						<StyledInnerContainer>
							<StyledUploadList>
								<LabelBold>{t.MOBILE_UPLOAD_DOCUMENTS_AFF_TITLE}</LabelBold>
								<StyledInput
									id="file"
									name="file"
									type="file"
									className="upload"
									size={FILE_SIZE}
									accept=".pdf, .gif, .jpg, .jpeg, .bmp, .png, .tif, .tiff"
									onChange={e => void onInputChange(e)}
									ref={inputRef}
								/>
								<StyledUploadButton disabled={isUploadLoading} onClick={onUpload} auto>
									{isUploadLoading && <Spinner />}
									{t.MOBILE_UPLOAD_DOCUMENTS_AFF_BUTTON}
								</StyledUploadButton>
							</StyledUploadList>
							{isFetchLoading ? (
								<Spinner />
							) : (
								<>
									{[...docs, ...documents].map((doc, index) => (
										<Fragment key={doc.id}>
											<StyledDocument
												key={doc.id}
												$animate={!doc.state && index === 0}
												className="u-divider"
											>
												<Box minWidth={0}>
													<Caption>
														{new Date(doc.playerTimestamp).toISOString().split('T')[0]}
													</Caption>
													<StyledFileName title={doc.filename}>{doc.filename}</StyledFileName>
												</Box>
												<StyledLabel>{doc.status}</StyledLabel>
											</StyledDocument>
										</Fragment>
									))}
								</>
							)}
						</StyledInnerContainer>
					</StyledDocumentContainer>
				</StyledScrollContainer>

				<StyledButtonContainer flexShrink={0}>
					<StyledContactSupport>
						<StyledOrText>{t.MOBILE_SPENDING_LIMIT_OR_TEXT}</StyledOrText>
						<StyledLinkButton
							onClick={onSupport}
							data-at="spending-limit-upload-contact-support"
							className="contact-btn"
						>
							{t.BUTTON_CUSTOMER_SUPPORT}
						</StyledLinkButton>
					</StyledContactSupport>
					<ButtonRoundedPrimary
						disabled={!docs.length}
						data-at="spending-limit-upload-continue"
						onClick={onContinue}
					>
						{t.MOBILE_UPLOAD_DOCUMENTS_AFF_BUTTON_PRIMARY}
					</ButtonRoundedPrimary>
				</StyledButtonContainer>
			</StyledContent>
		</StyledContainer>
	);
};

export default SpendingLimitUploadDocContent;
